// =============================================================================
// FILE: src/pages/patient/components/SupplementRegimenModal.tsx
// =============================================================================

import React, { useMemo, useCallback } from "react";
import { Modal, Tabs, Tag, Typography, Space, Badge, Divider, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DataTable, EmptyState } from "../../../components/shared";
import { SortAscendingOutlined, ClockCircleOutlined, LinkOutlined, PrinterOutlined } from "@ant-design/icons";
import { SupplementRegimen, SupplementRegimenItem, TimingSlot, ItemCategory } from "../../../types/healthcare/SupplementTypes";

const { Title, Text, Paragraph } = Typography;

// ─── Constants ────────────────────────────────────────────────────────────────

const TIMING_META: Record<TimingSlot, { label: string; icon: string; color: string }> = {
	morning: { label: "Morning", icon: "☀️", color: "#fadb14" },
	lunch: { label: "Lunch", icon: "🌤", color: "#73d13d" },
	dinner: { label: "Dinner", icon: "🌙", color: "#597ef7" },
	night: { label: "Night", icon: "⭐", color: "#9254de" },
	other: { label: "Other", icon: "🕐", color: "#8c8c8c" },
};

const TIMING_ORDER: TimingSlot[] = ["morning", "lunch", "dinner", "night", "other"];

const CATEGORY_PRIORITY: Record<ItemCategory, number> = {
	ESSENTIAL: 1,
	"STRONGLY RECOMMENDED": 2,
	BENEFICIAL: 3,
	"CURRENT - CONTINUE": 4,
	"CURRENT - DISCONTINUE": 5,
};

const CATEGORY_COLORS: Record<ItemCategory, string> = {
	ESSENTIAL: "red",
	"STRONGLY RECOMMENDED": "orange",
	BENEFICIAL: "green",
	"CURRENT - CONTINUE": "blue",
	"CURRENT - DISCONTINUE": "default",
};

// The print export builds raw HTML by concatenation, so every interpolated value must be escaped.
const esc = (v?: string) =>
	String(v ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#x27;");

// ─── Supplement cell ──────────────────────────────────────────────────────────

const SupplementCell: React.FC<{ item: SupplementRegimenItem }> = ({ item }) => (
	<Space direction="vertical" size={2} style={{ width: "100%" }}>
		<Text strong style={{ fontSize: 14 }}>
			{item.product_url ? (
				<a href={item.product_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--mg-text)" }}>
					{item.full_name}
					<LinkOutlined style={{ marginLeft: 5, fontSize: 11, opacity: 0.5 }} />
				</a>
			) : (
				item.full_name
			)}
		</Text>
		{item.description && (
			<Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
				{item.description}
			</Text>
		)}
	</Space>
);

// ─── Tab 1: By Priority ───────────────────────────────────────────────────────

const PriorityTab: React.FC<{ items: SupplementRegimenItem[] }> = ({ items }) => {
	const sorted = useMemo(
		() => [...items].sort((a, b) => (CATEGORY_PRIORITY[a.category] ?? 99) - (CATEGORY_PRIORITY[b.category] ?? 99) || a.full_name.localeCompare(b.full_name)),
		[items],
	);

	const groups = useMemo(() => {
		const g = new Map<ItemCategory, SupplementRegimenItem[]>();
		for (const item of sorted) {
			if (!g.has(item.category)) g.set(item.category, []);
			g.get(item.category)!.push(item);
		}
		return g;
	}, [sorted]);

	const columns: ColumnsType<SupplementRegimenItem> = [
		{
			title: "Supplement",
			key: "name",
			width: 450,
			render: (_, item) => <SupplementCell item={item} />,
		},
		{
			title: "Dosage",
			dataIndex: "dosage",
			key: "dosage",
			width: 130,
			render: (v: string) => (v ? <Text style={{ fontSize: 13 }}>{v}</Text> : <Text type="secondary">—</Text>),
		},
		{
			title: "When to Take",
			key: "timing",
			width: 120,
			render: (_, item) => {
				const m = TIMING_META[item.timing];
				return (
					<Space size={4}>
						<span>{m.icon}</span>
						<Text style={{ fontSize: 13 }}>{m.label}</Text>
					</Space>
				);
			},
		},
		{
			title: "Instructions",
			dataIndex: "instructions",
			key: "instructions",
			width: 180,
			render: (v: string) =>
				v ? (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{v}
					</Text>
				) : (
					<Text type="secondary">—</Text>
				),
		},
	];

	if (items.length === 0) {
		return <EmptyState title="No supplements in this regimen" />;
	}

	return (
		<div style={{ paddingTop: 8 }}>
			{Array.from(groups.entries()).map(([cat, catItems]) => (
				<div key={cat} style={{ marginBottom: 24 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
						<Tag color={CATEGORY_COLORS[cat]} style={{ margin: 0, fontWeight: 600, fontSize: 13, padding: "2px 10px" }}>
							{cat}
						</Tag>
						<Badge count={catItems.length} style={{ backgroundColor: "var(--mg-text-secondary)" }} />
					</div>
					<DataTable columns={columns} dataSource={catItems} rowKey="id" bare />
				</div>
			))}
		</div>
	);
};

// ─── Tab 2: Daily Schedule ────────────────────────────────────────────────────

const DailyScheduleTab: React.FC<{ regimen: SupplementRegimen }> = ({ regimen }) => {
	const columns: ColumnsType<SupplementRegimenItem & { _timing: TimingSlot }> = [
		{
			title: "Supplement",
			key: "name",
			width: 450,
			render: (_, item) => <SupplementCell item={item as SupplementRegimenItem} />,
		},
		{
			title: "Dosage",
			dataIndex: "dosage",
			key: "dosage",
			width: 130,
			render: (v: string) => (v ? <Text style={{ fontSize: 13 }}>{v}</Text> : <Text type="secondary">—</Text>),
		},
		{
			title: "Priority",
			dataIndex: "category",
			key: "category",
			width: 120,
			render: (cat: ItemCategory) => (
				<Tag color={CATEGORY_COLORS[cat]} style={{ fontSize: 12 }}>
					{cat}
				</Tag>
			),
		},
		{
			title: "Instructions",
			dataIndex: "instructions",
			key: "instructions",
			width: 180,
			render: (v: string) =>
				v ? (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{v}
					</Text>
				) : (
					<Text type="secondary">—</Text>
				),
		},
	];

	return (
		<div style={{ paddingTop: 8 }}>
			{TIMING_ORDER.map((timing) => {
				const slotItems = regimen.regimen?.[timing] || [];
				if (slotItems.length === 0) return null;

				const meta = TIMING_META[timing];
				const rows = slotItems.map((i) => ({ ...i, _timing: timing }));

				return (
					<div key={timing} style={{ marginBottom: 28 }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
								marginBottom: 10,
								padding: "8px 14px",
								borderRadius: 8,
								border: "1px solid var(--mg-border)",
								background: "var(--mg-bg-subtle)",
							}}
						>
							<span style={{ fontSize: 20 }}>{meta.icon}</span>
							<Text strong style={{ fontSize: 15 }}>
								{meta.label}
							</Text>
							<Badge count={slotItems.length} style={{ backgroundColor: "var(--mg-text-secondary)" }} />
						</div>

						<DataTable columns={columns} dataSource={rows} rowKey="id" bare />
					</div>
				);
			})}
			{TIMING_ORDER.every((t) => (regimen.regimen?.[t] || []).length === 0) && (
				<EmptyState title="No supplements in this regimen" />
			)}
		</div>
	);
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface SupplementRegimenModalProps {
	regimen: SupplementRegimen | null;
	visible: boolean;
	onClose: () => void;
}

const SupplementRegimenModal: React.FC<SupplementRegimenModalProps> = ({ regimen, visible, onClose }) => {
	const allItems = useMemo(() => (regimen ? TIMING_ORDER.flatMap((t) => regimen.regimen?.[t] || []) : []), [regimen]);
	const totalCount = allItems.length;

	const handlePrint = useCallback(() => {
		if (!regimen) return;

		const slotsHtml = TIMING_ORDER.map((timing) => {
			const items = regimen.regimen?.[timing] || [];
			if (items.length === 0) return "";
			const meta = TIMING_META[timing];
			const rowsHtml = items
				.map(
					(item) => `
				<tr>
					<td>${item.product_url ? `<a href="${esc(item.product_url)}">${esc(item.full_name)}</a>` : esc(item.full_name)}${item.description ? `<br/><small>${esc(item.description)}</small>` : ""}</td>
					<td>${esc(item.dosage) || "—"}</td>
					<td>${esc(item.instructions) || "—"}</td>
					<td>${esc(item.category)}</td>
				</tr>`,
				)
				.join("");
			return `
				<div class="time-block">
					<h3>${meta.icon} ${meta.label} <span class="badge">${items.length}</span></h3>
					<table>
						<thead><tr><th>Supplement</th><th>Dosage</th><th>Instructions</th><th>Priority</th></tr></thead>
						<tbody>${rowsHtml}</tbody>
					</table>
				</div>`;
		})
			.filter(Boolean)
			.join("");

		const html = `<!DOCTYPE html>
			<html>
			<head>
				<meta charset="utf-8" />
				<title>${esc(regimen.name) || "Supplement Regimen"}</title>
				<style>
					body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
					h1 { font-size: 22px; margin: 0 0 4px; }
					.subtitle { color: #666; font-size: 14px; margin-bottom: 6px; }
					.summary { font-size: 13px; color: #555; margin-bottom: 20px; border-left: 3px solid #d9d9d9; padding-left: 10px; }
					.time-block { margin-bottom: 28px; }
					.time-block h3 { font-size: 15px; margin: 0 0 8px; padding: 8px 12px; background: #f8f8f8; border-radius: 4px; }
					.badge { display: inline-block; background: #8c8c8c; color: #fff; border-radius: 10px; padding: 1px 8px; font-size: 12px; margin-left: 6px; }
					table { width: 100%; border-collapse: collapse; font-size: 13px; }
					th { background: #f0f0f0; text-align: left; padding: 7px 10px; border: 1px solid #d9d9d9; font-weight: 600; }
					td { padding: 7px 10px; border: 1px solid #d9d9d9; vertical-align: top; }
					tr:nth-child(even) td { background: #fafafa; }
					td small { display: block; margin-top: 3px; color: #888; font-size: 11px; }
					a { color: #1677ff; }
					.footer { margin-top: 32px; font-size: 11px; color: #aaa; border-top: 1px solid #f0f0f0; padding-top: 10px; }
					@media print { body { padding: 8px; } a { color: #000; } }
				</style>
			</head>
			<body>
				<h1>${esc(regimen.name) || "Supplement Regimen"}</h1>
				<div class="subtitle">${totalCount} supplement${totalCount !== 1 ? "s" : ""}</div>
				${regimen.summary ? `<div class="summary">${esc(regimen.summary)}</div>` : ""}
				${slotsHtml}
				<div class="footer">Generated ${new Date().toLocaleDateString()}</div>
			</body>
			</html>`;

		const printWindow = window.open("", "_blank");
		if (!printWindow) return;
		printWindow.document.write(html);
		printWindow.document.close();
		printWindow.focus();
		printWindow.print();
	}, [regimen, totalCount]);

	const tabItems = [
		{
			key: "schedule",
			label: (
				<Space size={6}>
					<ClockCircleOutlined />
					My Daily Regimen
				</Space>
			),
			children: regimen ? <DailyScheduleTab regimen={regimen} /> : null,
		},
		{
			key: "priority",
			label: (
				<Space size={6}>
					<SortAscendingOutlined />
					By Priority
				</Space>
			),
			children: regimen ? <PriorityTab items={allItems} /> : null,
		},
	];

	return (
		<Modal
			open={visible}
			onCancel={onClose}
			destroyOnClose
			footer={null}
			width={1100}
			style={{ top: 24 }}
			styles={{
				body: { padding: "0 24px 24px", maxHeight: "calc(90vh - 120px)", overflow: "auto" },
				header: { padding: "16px 24px 0" },
			}}
			title={
				<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingRight: 32 }}>
					<div>
						<Title level={4} style={{ margin: 0 }}>
							{regimen?.name || "Supplement Regimen"}
						</Title>
						<Space size={8} style={{ marginTop: 4 }}>
							<Text type="secondary" style={{ fontSize: 13 }}>
								{totalCount} supplement{totalCount !== 1 ? "s" : ""}
							</Text>
						</Space>
						{regimen?.summary && (
							<Paragraph type="secondary" style={{ fontSize: 13, margin: "8px 0 0", lineHeight: 1.6, fontWeight: 400 }}>
								{regimen.summary}
							</Paragraph>
						)}
					</div>
					<Button
						icon={<PrinterOutlined />}
						onClick={handlePrint}
						disabled={!regimen || totalCount === 0}
						style={{ marginTop: 2, flexShrink: 0 }}
					>
						Print / Save PDF
					</Button>
				</div>
			}
		>
			<Divider style={{ margin: "12px 0 0" }} />
			<Tabs items={tabItems} size="large" style={{ marginTop: 4 }} />
		</Modal>
	);
};

export default SupplementRegimenModal;
